import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn, fuzzyMatchVi } from '@/lib/utils';

export interface FilterOption {
  value: string;
  label: string;
}

interface SearchableFilterDropdownProps {
  options: FilterOption[];
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  isMulti?: boolean;
  className?: string;
}

export function SearchableFilterDropdown({
  options = [],
  value,
  onChange,
  placeholder = 'Tất cả',
  isMulti = false,
  className,
}: SearchableFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedValues = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.length > 0) return [value];
    return [];
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter(opt => 
      fuzzyMatchVi(opt.label, search) || 
      fuzzyMatchVi(opt.value, search)
    );
  }, [options, search]);

  const triggerLabel = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const match = options.find(o => o.value === selectedValues[0]);
      return match ? match.label : selectedValues[0];
    }
    const firstMatch = options.find(o => o.value === selectedValues[0]);
    const firstLabel = firstMatch ? firstMatch.label : selectedValues[0];
    return `${firstLabel} (+${selectedValues.length - 1})`;
  }, [selectedValues, options, placeholder]);

  const handleSelect = (val: string) => {
    if (isMulti) {
      const exists = selectedValues.includes(val);
      const next = exists 
        ? selectedValues.filter(v => v !== val)
        : [...selectedValues, val];
      onChange(next);
    } else {
      if (selectedValues[0] === val) {
        onChange('');
      } else {
        onChange(val);
      }
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(isMulti ? [] : '');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "h-6 text-[10px] w-full border border-slate-200 bg-white rounded shadow-none px-1.5 py-0",
            "inline-flex items-center justify-between gap-1 text-slate-700 hover:bg-slate-50 transition-colors",
            "focus:outline-none focus:ring-1 focus:ring-indigo-300",
            selectedValues.length > 0 && "font-semibold text-indigo-700 bg-indigo-50/50 border-indigo-200",
            className
          )}
        >
          <span className="truncate text-left flex-1">{triggerLabel}</span>
          <div className="flex items-center shrink-0 gap-0.5 text-slate-400">
            {selectedValues.length > 0 && (
              <span 
                role="button" 
                tabIndex={0}
                onClick={handleClear}
                className="hover:text-rose-600 p-0.5 rounded cursor-pointer"
                title="Xóa lọc"
              >
                <X className="h-2.5 w-2.5" />
              </span>
            )}
            <ChevronDown className="h-2.5 w-2.5 opacity-60" />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent 
        align="start" 
        className="w-56 p-1.5 shadow-lg border-slate-200 bg-white z-[70] text-xs"
        sideOffset={4}
      >
        {/* Search input */}
        <div className="relative mb-1">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm kiếm..."
            className="h-7 text-xs pl-7 pr-2 py-1 bg-slate-50 border-slate-200 focus:bg-white"
            autoFocus
          />
        </div>

        {/* Clear All / Default option */}
        <div className="space-y-0.5 max-h-48 overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              onChange(isMulti ? [] : '');
              if (!isMulti) setOpen(false);
            }}
            className={cn(
              "w-full text-left px-2 py-1 rounded text-xs flex items-center justify-between transition-colors",
              selectedValues.length === 0 
                ? "bg-indigo-50 text-indigo-700 font-medium" 
                : "text-slate-600 hover:bg-slate-100"
            )}
          >
            <span>Tất cả</span>
            {selectedValues.length === 0 && <Check className="h-3.5 w-3.5 text-indigo-600" />}
          </button>

          {filteredOptions.length === 0 ? (
            <div className="py-3 text-center text-slate-400 text-xs italic">
              Không tìm thấy lựa chọn
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = selectedValues.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={cn(
                    "w-full text-left px-2 py-1 rounded text-xs flex items-center justify-between transition-colors",
                    isSelected 
                      ? "bg-indigo-50 text-indigo-700 font-medium" 
                      : "text-slate-700 hover:bg-slate-100"
                  )}
                >
                  <span className="truncate pr-1">{opt.label}</span>
                  {isSelected && <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableFilterDropdown;
